/**
 * Story 30.3 — push subscriptions on the harness.
 *
 *   GET    /api/push/config            → { available, publicKey|null, platformEnabled, tenantPaused }
 *   GET    /api/push/subscriptions     → [{ id, label, createdAt, lastSuccessAt, endpointSha256 }]
 *   POST   /api/push/subscriptions     { endpoint, keys: { p256dh, auth }, replacesEndpoint? } → 201 { id }
 *   DELETE /api/push/subscriptions/:id → 204 (own rows only, else 404)
 *
 * Every role. The server never returns an endpoint: the SPA hashes its own
 * (`src/lib/push/subscribe.ts` `endpointSha256`) to find "this device".
 *
 * Through the shared `harnessClient` (bearer attach + 401 refresh replay),
 * EXCEPT the two `…Bare` calls used by `logout()`: those go through bare
 * axios with the current bearer, because a refresh triggered during logout
 * would mint a new refresh cookie right as logout revokes the old one
 * (`src/lib/api/auth.ts`).
 */

import axios from 'axios';
import { harnessClient, HARNESS_BASE_URL } from '@/lib/api/client';

export const PUSH_CONFIG_PATH = '/api/push/config';
export const PUSH_SUBSCRIPTIONS_PATH = '/api/push/subscriptions';

export interface PushConfig {
  /** VAPID is configured on the server (devices can subscribe). */
  available: boolean;
  publicKey: string | null;
  /** The platform kill switch (`PUSH_ENABLED`). */
  platformEnabled: boolean;
  /** "Pause push for everyone" (`tenants.push_paused`). */
  tenantPaused: boolean;
}

export interface PushDevice {
  id: string;
  label: string | null;
  createdAt: string;
  lastSuccessAt: string | null;
  endpointSha256: string;
}

export interface PushSubscribeBody {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  replacesEndpoint?: string;
}

export async function fetchPushConfig(signal?: AbortSignal): Promise<PushConfig> {
  const res = await harnessClient.get<PushConfig>(PUSH_CONFIG_PATH, { signal, skipErrorToast: true });
  return res.data;
}

export async function fetchPushSubscriptions(signal?: AbortSignal): Promise<PushDevice[]> {
  const res = await harnessClient.get<PushDevice[]>(PUSH_SUBSCRIPTIONS_PATH, {
    signal,
    skipErrorToast: true,
  });
  return res.data;
}

export async function postPushSubscription(body: PushSubscribeBody): Promise<{ id: string }> {
  const res = await harnessClient.post<{ id: string }>(PUSH_SUBSCRIPTIONS_PATH, body, {
    skipErrorToast: true,
  });
  return res.data;
}

export async function deletePushSubscription(id: string): Promise<void> {
  await harnessClient.delete(`${PUSH_SUBSCRIPTIONS_PATH}/${encodeURIComponent(id)}`, {
    skipErrorToast: true,
  });
}

/** Logout only: bare axios, no refresh interceptor. */
export async function fetchPushSubscriptionsBare(
  accessToken: string,
  signal?: AbortSignal,
): Promise<PushDevice[]> {
  const res = await axios.get<PushDevice[]>(`${HARNESS_BASE_URL}${PUSH_SUBSCRIPTIONS_PATH}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
  return Array.isArray(res.data) ? res.data : [];
}

/** Logout only: bare axios, no refresh interceptor. */
export async function deletePushSubscriptionBare(
  id: string,
  accessToken: string,
  signal?: AbortSignal,
): Promise<void> {
  await axios.delete(`${HARNESS_BASE_URL}${PUSH_SUBSCRIPTIONS_PATH}/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
}
